import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import TableRowsRoundedIcon from '@mui/icons-material/TableRowsRounded';
import {
  Box,
  Card,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  styled,
  toggleButtonGroupClasses,
} from '@mui/material';
import { TabPanel } from 'components/common/TabPanel';
import { CreateEventButton } from 'components/event/CreateEventButton';
import { EventList, EventListLayout } from 'components/event/EventList';
import { useFirebase } from 'contexts/FirebaseContext';
import dayjs from 'dayjs';
import { collection, doc, getDocs, setDoc } from 'firebase/firestore';
import { useRequests } from 'hooks/useRequests';
import { useUser } from 'hooks/useUser';
import { EventOccasion } from 'models/EventOccasion';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
// import { useNavigate } from 'react-router-dom';

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  [`& .${toggleButtonGroupClasses.grouped}`]: {
    margin: theme.spacing(0.5),
    border: 0,
    borderRadius: theme.shape.borderRadius,
    [`&.${toggleButtonGroupClasses.disabled}`]: {
      border: 0,
    },
  },
  [`& .${toggleButtonGroupClasses.middleButton},& .${toggleButtonGroupClasses.lastButton}`]: {
    marginLeft: -1,
    borderLeft: '1px solid transparent',
  },
}));

export function Events() {
  const { t } = useTranslation();
  const db = useFirebase().db;
  // const navigate = useNavigate();
  const { user } = useUser();
  const [eventList, setEventList] = useState<EventOccasion[]>([]);
  const [tabIndex, setTabIndex] = useState(0);
  const [layout, setLayout] = useState<EventListLayout>(EventListLayout.Grid);
  const { fetchRequests } = useRequests(user?.uid);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // if (null == userContext.user) navigate('/auth'); // redirect to login / register if no user is logged in

  useEffect(() => {
    if (!user) return;

    const loadEvents = async () => {
      const eventsCollection = collection(db, `user/${user.uid}/events`);
      const eventsSnapshot = await getDocs(eventsCollection);

      let eventsData = await Promise.all(
        eventsSnapshot.docs.map(async (doc) => {
          return {
            ...(doc.data() as EventOccasion),
            id: doc.id,
            requests: await fetchRequests(doc.id),
          };
        })
      );

      setEventList(eventsData);
    };
    loadEvents();
  }, [db, user]);

  const updateEvent = async (event: EventOccasion) => {
    if (event.id && user) {
      setDoc(doc(db, `user/${user.uid}/events`, event.id), { ...event }, { merge: true });
    }
  };

  const handleDeleteEvent = (deleteEvent: EventOccasion) => {
    deleteEvent.isDeleted = true;
    deleteEvent.deleteDate = dayjs().toISOString();
    updateEvent(deleteEvent);

    const nextEventList = eventList.map((event) => {
      if (event.id === deleteEvent.id) {
        return deleteEvent;
      }

      return event;
    });

    setEventList(nextEventList);
  };

  const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const handleRestoreEvent = async (restoredEvent: EventOccasion) => {
    restoredEvent = { ...restoredEvent, isDeleted: false };

    if (restoredEvent.id && user) {
      setDoc(
        doc(db, `user/${user.uid}/events`, restoredEvent.id),
        {
          isDeleted: false,
        },
        { merge: true }
      );
    }

    setEventList(eventList.map((event) => (event.id == restoredEvent.id ? restoredEvent : event)));
  };

  return (
    <>
      <Typography variant="h1" gutterBottom>
        {t('yourEvents', 'Your Events')}
      </Typography>
      <Box marginBottom={2}>
        <CreateEventButton />
      </Box>
      <Card
        elevation={0}
        sx={(theme) => ({
          border: 1,

          borderColor: 'divider',
          display: 'flex',
          ...(layout == EventListLayout.Table && {
            borderEndStartRadius: 0,
            borderEndEndRadius: 0,
          }),
        })}
      >
        <Tabs value={tabIndex} onChange={handleChangeTab}>
          <Tab label={t('upcoming', 'Upcoming')} />
          <Tab label={t('completed', 'Completed')} />
          <Tab label={t('trash', 'Trash')} />
        </Tabs>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', ml: 'auto' }}>
          <StyledToggleButtonGroup
            value={layout}
            exclusive
            onChange={(e, value) => setLayout(value)}
            aria-label={t('eventListLayout', 'Event list layout')}
          >
            <ToggleButton value={EventListLayout.Grid} aria-label={t('grid', 'Grid')} size="small">
              <GridViewRoundedIcon />
            </ToggleButton>
            <ToggleButton
              value={EventListLayout.Table}
              aria-label={t('table', 'Table')}
              size="small"
            >
              <TableRowsRoundedIcon />
            </ToggleButton>
          </StyledToggleButtonGroup>
        </Box>
      </Card>
      <Box
        sx={{
          border: layout == EventListLayout.Grid ? 0 : 1,
          borderColor: 'divider',
          borderRadius: 2,
          borderTop: 0,
          borderStartStartRadius: 0,
          borderStartEndRadius: 0,
        }}
      >
        <TabPanel index={0} value={tabIndex}>
          <EventList
            layout={layout}
            eventList={eventList.filter(
              (event) => !event.isDeleted && new Date(event.date) >= today
            )}
            onDeleteEvent={handleDeleteEvent}
            onRestoreEvent={handleRestoreEvent}
          />
        </TabPanel>
        <TabPanel index={1} value={tabIndex}>
          <EventList
            layout={layout}
            eventList={eventList.filter(
              (event) => !event.isDeleted && new Date(event.date) < today
            )}
            onDeleteEvent={handleDeleteEvent}
            onRestoreEvent={handleRestoreEvent}
          />
        </TabPanel>
        <TabPanel index={2} value={tabIndex}>
          <EventList
            layout={layout}
            eventList={eventList.filter((event) => event.isDeleted)}
            onRestoreEvent={handleRestoreEvent}
          />
        </TabPanel>
      </Box>
    </>
  );
}
